import { useMemo, useState } from 'react';

import { format as formatDate } from 'date-fns';
import styled from 'styled-components';

import { Badge, BadgeVariant } from '@components/badge';
import { Card } from '@components/card';
import { IconDate } from '@components/icons/date';
import { hideModal, Modal, useModal } from '@components/modal';
import { Select } from '@components/select';
import { Stack } from '@components/stack';
import { TextField } from '@components/text-field';
import { Text } from '@components/text/text';
import { getScopedTranslate, useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import {
  CompositionItem,
  DEFAULT_FORMAT_LANGUAGE,
  FORMAT_LANGUAGE_OPTIONS,
  FORMAT_TOKEN_BADGES,
  FormatTokenBadge,
  SAMPLE_DATE,
  SeparatorBadge,
  TokenCategory,
  generateCompositionId,
  getDateFnsLocale,
  getSeparatorBadges,
  parseFormatString,
} from './format-presets';

const getDatePreview = (formatString: string, language?: string): string => {
  if (!formatString) return '';
  try {
    const locale = language ? getDateFnsLocale(language) : undefined;
    return formatDate(SAMPLE_DATE, formatString, locale ? { locale } : undefined);
  } catch {
    return getScopedTranslate(locale).t('invalidFormat');
  }
};

const getTokenExample = (token: string, language?: string): string => {
  const locale = language ? getDateFnsLocale(language) : undefined;
  return formatDate(SAMPLE_DATE, token, locale ? { locale } : undefined);
};

const CATEGORY_VARIANT: Record<TokenCategory, BadgeVariant> = {
  day: 'blue',
  month: 'indigo',
  year: 'green',
};

type TokenWithExample = FormatTokenBadge & { example: string };

const TokenBadgeRow = ({
  tokens,
  onAdd,
}: {
  tokens: TokenWithExample[];
  onAdd: (token: FormatTokenBadge) => void;
}) => (
  <BadgeRow>
    {tokens.map((token) => (
      <Badge
        key={token.display}
        text={`${token.display} • ${token.example}`}
        variant={CATEGORY_VARIANT[token.category]}
        size="md"
        isInteractive
        onClick={() => onAdd(token)}
      />
    ))}
  </BadgeRow>
);

const LANGUAGE_SELECT_OPTIONS = FORMAT_LANGUAGE_OPTIONS.map((option) => ({
  value: option.value,
  label: option.label,
}));

type FormatPickerModalProps = {
  onApply: (format: string, formatLanguage?: string, name?: string) => void;
  onSavePreset?: (preset: { name: string; format: string; formatLanguage?: string }) => void;
  initialFormat?: string;
  initialLanguage?: string;
  initialName?: string;
  isEditing?: boolean;
};

const FormatPickerModal = ({
  onApply,
  onSavePreset,
  initialFormat,
  initialLanguage,
  initialName,
  isEditing,
}: FormatPickerModalProps) => {
  const { t } = useScopedTranslate(locale);
  const [formatName, setFormatName] = useState(initialName ?? '');
  const [composition, setComposition] = useState<CompositionItem[]>(() =>
    parseFormatString(initialFormat ?? ''),
  );
  const [language, setLanguage] = useState(initialLanguage ?? DEFAULT_FORMAT_LANGUAGE);

  const formatString = composition.map((item) => item.value).join('');

  const isDirty =
    !isEditing ||
    formatString !== initialFormat ||
    (language ?? '') !== (initialLanguage ?? '') ||
    formatName !== initialName;

  const preview = useMemo(() => getDatePreview(formatString, language), [formatString, language]);

  const tokenExamples = useMemo(
    () =>
      FORMAT_TOKEN_BADGES.map((token) => ({
        ...token,
        example: getTokenExample(token.token, language),
      })),
    [language],
  );

  const addToken = (token: FormatTokenBadge) => {
    setComposition((previous) => [
      ...previous,
      {
        id: generateCompositionId(),
        type: 'token',
        display: token.display,
        value: token.token,
        category: token.category,
      },
    ]);
  };

  const addSeparator = (separator: SeparatorBadge) => {
    setComposition((previous) => [
      ...previous,
      {
        id: generateCompositionId(),
        type: 'separator',
        display: separator.display,
        value: separator.value,
      },
    ]);
  };

  const removeItem = (id: string) => {
    setComposition((previous) => previous.filter((item) => item.id !== id));
  };

  const hasConflict = (item: CompositionItem, index: number) => {
    if (item.type !== 'token' || !item.category) return false;
    const previous = composition[index - 1];
    return previous?.type === 'token' && previous?.category === item.category;
  };

  const hasAnyConflict = composition.some((item, index) => hasConflict(item, index));
  const hasDateToken = composition.some((item) => item.type === 'token');

  const handleSave = () => {
    if (formatString) {
      onApply(formatString, language, formatName || undefined);
      if (formatName && onSavePreset) {
        onSavePreset({
          name: formatName,
          format: formatString,
          formatLanguage: language,
        });
      }
      hideModal();
    }
  };

  const dayTokens = tokenExamples.filter((token) => token.category === 'day');
  const monthTokens = tokenExamples.filter((token) => token.category === 'month');
  const yearTokens = tokenExamples.filter((token) => token.category === 'year');

  return (
    <Modal>
      <Modal.SimpleHeader title={isEditing ? t('titleEdit') : t('titleCreate')} />
      <Modal.Content>
        <Stack space={4}>
          <TopRow>
            <TextField
              label={t('formatName')}
              value={formatName}
              onChange={setFormatName}
              placeholder={t('formatNamePlaceholder')}
              maxChars={255}
              autoFocus
            />
            <Select
              label={t('language')}
              options={LANGUAGE_SELECT_OPTIONS}
              value={language}
              onChange={(value) => setLanguage(value)}
              isClearable={false}
            />
          </TopRow>

          <Stack space={1}>
            <Text text={t('date')} font="bold14" />
            <TokenBadgeRow tokens={dayTokens} onAdd={addToken} />
            <TokenBadgeRow tokens={monthTokens} onAdd={addToken} />
            <TokenBadgeRow tokens={yearTokens} onAdd={addToken} />
          </Stack>

          <Stack space={1}>
            <Text text={t('separators')} font="bold14" />
            <BadgeRow>
              {getSeparatorBadges().map((separator) => (
                <Badge
                  key={separator.display}
                  text={separator.display}
                  variant="purple"
                  size="md"
                  isInteractive
                  onClick={() => addSeparator(separator)}
                />
              ))}
            </BadgeRow>
          </Stack>

          <Stack space={1}>
            <Text text={t('composition')} font="bold14" />
            <CompositionInput>
              {composition.length === 0 ? (
                <Text
                  text={t('compositionPlaceholder')}
                  font="regular14"
                  color="textHelper"
                  style={{ paddingLeft: pxToRem(4) }}
                />
              ) : (
                composition.map((item, index) => {
                  const isConflict = hasConflict(item, index);
                  return (
                    <Badge
                      key={item.id}
                      text={item.display}
                      variant={
                        isConflict
                          ? 'red'
                          : item.category
                            ? CATEGORY_VARIANT[item.category]
                            : 'purple'
                      }
                      tooltip={isConflict ? t('conflictTooltip') : undefined}
                      size="md"
                      onRemove={() => removeItem(item.id)}
                    />
                  );
                })
              )}
            </CompositionInput>
          </Stack>

          <Card variant="indigo" space={4} style={{ borderColor: theme.color.border10 }}>
            <PreviewRow>
              <PreviewLeft>
                <IconDate size={24} color="elementIndigo100" />
                <Text
                  text={
                    hasAnyConflict
                      ? t('invalidFormat')
                      : formatString
                        ? preview
                        : t('addTokensToPreview')
                  }
                  font="semibold14"
                  color="elementIndigo100"
                />
              </PreviewLeft>
              <Text text={t('preview')} font="semibold12" color="textHelper" />
            </PreviewRow>
          </Card>
        </Stack>
      </Modal.Content>
      <Modal.SimpleFooter
        onSave={handleSave}
        isSaveDisabled={
          !formatString || !formatName.trim() || !isDirty || hasAnyConflict || !hasDateToken
        }
        translationsOverride={{ save: t('save') }}
      />
    </Modal>
  );
};

export const useFormatPickerModal = () => useModal(FormatPickerModal, { variant: 'LARGE' });

const TopRow = styled.div({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: pxToRem(16),
});

const BadgeRow = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  gap: pxToRem(8),
});

const CompositionInput = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: pxToRem(6),
  minHeight: theme.base.height,
  padding: pxToRem(8),
  borderRadius: pxToRem(8),
  backgroundColor: theme.color.uiTrans5,
});

const PreviewRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const PreviewLeft = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: pxToRem(12),
});

const locale = {
  en: {
    titleCreate: 'Create new date format',
    titleEdit: 'Edit date format',
    formatName: 'Format name',
    formatNamePlaceholder: 'Name your date format...',
    language: 'Language',
    date: 'Date',
    separators: 'Separators',
    composition: 'Composition',
    compositionPlaceholder: 'Click tokens to add...',
    preview: 'Preview',
    addTokensToPreview: 'Add tokens to preview',
    invalidFormat: 'Invalid format',
    conflictTooltip: 'Two tokens of the same type cannot be placed next to each other',
    save: 'Save',
  },
};
